/**
 * Shared Coverage Matrix status tokens + field provenance chips.
 * Used by CoverageMatrix (compact table) and CoverageRequirementPanel.
 */
import React from 'react';
import type { FieldProvenanceKind } from '../../../lib/evidenceLineage/requirementToFields';

export type CoverageCellStatus = 'strong' | 'adequate' | 'authored' | 'waived' | 'tentative' | 'missing';

export const CELL_CLASS: Record<CoverageCellStatus, string> = {
  strong:    'bg-success/10 text-success border-success/40',
  adequate:  'bg-primary/10 text-primary border-primary/40',
  tentative: 'bg-warning/10 text-warning border-warning/40',
  // Authored = a HUMAN-written claim. It previously shared the `info` token,
  // which the Aurora retheme collapsed onto the same teal as `primary`
  // (Adequate) — so the two read identically. Move Authored onto the violet
  // "manual provenance" treatment already used by FieldProvenanceChip below so
  // it is unmistakably distinct from Adequate's teal.
  authored:  'bg-violet-500/10 text-violet-500 border-violet-500/40',
  waived:    'border border-amber-500/30 bg-amber-500/5 text-text-secondary',
  missing:   'bg-state-hover text-text-muted border-border',
};

export const CELL_DOT_CLASS: Record<CoverageCellStatus, string> = {
  strong:   'bg-success',
  adequate: 'bg-primary',
  authored: 'bg-violet-500',
  waived:   'bg-amber-400/60',
  tentative:'bg-warning',
  missing:  'bg-surface-3',
};

export const CELL_GLYPH: Record<CoverageCellStatus, string> = {
  strong:    '✓',
  adequate:  '~',
  tentative: '?',
  authored:  '✎',
  waived:    '~',
  missing:   '—',
};

/**
 * Single-character BADGE glyph for the compact tier squares (legend + domain
 * rollups). It is the first letter of the label EXCEPT for `authored`, which
 * would otherwise collide with `adequate` (both 'A'); the ✎ pen mark keeps it
 * distinct and reads as "hand-authored". Surfaces should use this, NOT
 * `CELL_LABEL[status].charAt(0)`, so the A/A collision can never reappear.
 */
export const CELL_INITIAL: Record<CoverageCellStatus, string> = {
  strong:    'S',
  adequate:  'A',
  tentative: 'T',
  authored:  '✎',
  waived:    'W',
  missing:   'M',
};

export const CELL_LABEL: Record<CoverageCellStatus, string> = {
  strong:    'Strong',
  adequate:  'Adequate',
  tentative: 'Tentative',
  authored:  'Authored',
  waived:    'Waived',
  missing:   'Missing',
};

/**
 * Readiness-console display order — best to worst. Drives the console
 * status-mix line, the lane roll-up bars, the top-edge distribution
 * strip, and the legend, so all four surfaces read in the same order.
 */
export const CELL_STATUS_ORDER: readonly CoverageCellStatus[] = [
  'strong',
  'adequate',
  'authored',
  'waived',
  'tentative',
  'missing',
];

/**
 * Solid fill token per status — used for the lane roll-up coverage bars,
 * the console top-edge distribution strip, and the legend swatches.
 * Mirrors CELL_CLASS hues at full opacity (authored stays the violet
 * "manual provenance" treatment; missing is a muted hairline tone so a
 * bar of gaps never reads as an alarm wall).
 */
export const CELL_BAR_CLASS: Record<CoverageCellStatus, string> = {
  strong:    'bg-success',
  adequate:  'bg-primary',
  tentative: 'bg-warning',
  authored:  'bg-violet-500',
  waived:    '',
  missing:   'bg-border-strong',
};

export const SOURCE_SUMMARY_ROWS: ReadonlyArray<{
  kind: 'dam' | 'provider' | 'doc' | 'authored' | 'research' | 'prior';
  icon: string;
  label: string;
  plural: string;
}> = [
  { kind: 'dam', icon: '🤖', label: 'DAM', plural: 'DAM refs' },
  { kind: 'provider', icon: '🔌', label: 'API provider', plural: 'API providers' },
  { kind: 'doc', icon: '📄', label: 'Document', plural: 'Documents' },
  { kind: 'authored', icon: '✍', label: 'Authored', plural: 'Authored claims' },
  { kind: 'research', icon: '🔍', label: 'AI research', plural: 'AI research' },
  { kind: 'prior', icon: '📊', label: 'Prior report', plural: 'Prior reports' },
];

const FIELD_PROV_LABEL: Record<FieldProvenanceKind, { icon: string; label: string; tone: string }> = {
  provider:          { icon: '🔌', label: 'provider', tone: 'border-primary/40 bg-primary/5 text-text-primary' },
  doc:               { icon: '📄', label: 'doc', tone: 'border-text-muted/40 bg-state-hover text-text-primary' },
  'doc-override':    { icon: '📄', label: 'doc', tone: 'border-text-muted/40 bg-state-hover text-text-primary' },
  'provider+doc':    { icon: '🔌', label: 'API+doc', tone: 'border-primary/40 bg-primary/5 text-text-primary' },
  authored:          { icon: '✍', label: 'authored', tone: 'border-info/40 bg-info/5 text-text-primary' },
  derived:           { icon: '⚡', label: 'derived', tone: 'border-warning/40 bg-warning/5 text-text-primary' },
  'report-derived':  { icon: '📊', label: 'prior', tone: 'border-violet-500/40 bg-violet-500/5 text-text-primary' },
  'verified-from-kmz': { icon: '🗺', label: 'KMZ', tone: 'border-emerald-500/40 bg-emerald-500/5 text-text-primary' },
  research:          { icon: '🔍', label: 'research', tone: 'border-warning/40 bg-warning/5 text-text-primary' },
  dam:               { icon: '🤖', label: 'DAM', tone: 'border-violet-500/40 bg-violet-500/5 text-text-primary' },
  unknown:           { icon: '·', label: 'unknown', tone: 'border-border-subtle bg-surface-2 text-text-muted' },
};

export function FieldProvenanceChip({
  provenance,
  sourceLabel,
}: {
  provenance: FieldProvenanceKind;
  /** Optional one-line source (e.g. "USGS") shown in the chip tooltip. */
  sourceLabel?: string;
}): React.ReactElement {
  const meta = FIELD_PROV_LABEL[provenance];
  const title = sourceLabel
    ? `Provenance: ${meta.label} — ${sourceLabel}`
    : `Provenance: ${meta.label}`;
  return (
    <span
      data-testid={`evl-field-prov-${provenance}`}
      className={`inline-flex items-center gap-0.5 px-1 py-px border ${meta.tone} type-meta-strong`}
      title={title}
    >
      <span aria-hidden="true">{meta.icon}</span>
      {meta.label}
    </span>
  );
}
